/**
 * Pure (React-free) helpers for the beneficiary allocation block. Kept separate
 * from BeneficiaryAllocation.tsx so they can be unit-tested in a node env and
 * reused by both enrollment surfaces (DEV-177) without pulling in the UI.
 * BeneficiaryAllocation.tsx re-exports the public ones for backward compat.
 */

export interface BeneficiaryRow {
  firstName: string;
  lastName: string;
  relationship: string;
  /** integer 0..100 */
  pct: number;
}

/**
 * Wrapped in an object so the future addition of contingent (or other tiers) is
 * an additive shape change — and so call sites can keep using `.primary` as the
 * one canonical accessor.
 */
export interface BeneficiaryAllocationValue {
  primary: BeneficiaryRow[];
}

export const emptyRow = (pct: number): BeneficiaryRow => ({
  firstName: "",
  lastName: "",
  relationship: "",
  pct,
});

export function defaultBeneficiaryAllocation(): BeneficiaryAllocationValue {
  return { primary: [emptyRow(100)] };
}

export const rowComplete = (b: BeneficiaryRow): boolean =>
  !!b.firstName.trim() && !!b.lastName.trim() && !!b.relationship;

export const sumPct = (rows: BeneficiaryRow[]): number =>
  rows.reduce((a, b) => a + (Number(b.pct) || 0), 0);

export function isBeneficiaryAllocationValid(
  value: BeneficiaryAllocationValue | null | undefined,
): boolean {
  if (!value || value.primary.length === 0) return false;
  return sumPct(value.primary) === 100 && value.primary.every(rowComplete);
}

/** Map the controlled value to the `/beneficiaries/replace` payload shape. */
export function toInsertBeneficiaries(
  value: BeneficiaryAllocationValue,
  ctx: { enrollmentId: number; employeeId: number; planId: number },
): Array<Record<string, any>> {
  return value.primary
    .filter((b) => b.firstName.trim() || b.lastName.trim() || b.relationship)
    .map((b, i) => ({
      enrollmentId: ctx.enrollmentId,
      employeeId: ctx.employeeId,
      planId: ctx.planId,
      beneficiaryType: "primary",
      sequenceOrder: i + 1,
      firstName: b.firstName.trim(),
      lastName: b.lastName.trim(),
      relationship: b.relationship,
      percentage: Number(b.pct) || 0,
    }));
}

/**
 * Mirror primary[0] into the legacy `enrollments.beneficiaryName` /
 * `beneficiaryRelationship` pair. The legacy columns are kept in sync with the
 * first primary beneficiary so reports / forms that haven't migrated to the
 * dedicated table still render something sensible.
 */
export function mirrorPrimaryToLegacy(
  value: BeneficiaryAllocationValue | null | undefined,
): { beneficiaryName: string | null; beneficiaryRelationship: string | null } {
  const p = value?.primary?.[0];
  if (!p) return { beneficiaryName: null, beneficiaryRelationship: null };
  const name = `${p.firstName} ${p.lastName}`.trim() || null;
  return { beneficiaryName: name, beneficiaryRelationship: p.relationship || null };
}

/** Seed the block from saved DB rows (GET /api/enrollments/:id/beneficiaries). */
export function fromBeneficiaryRows(
  rows: any[] | null | undefined,
): BeneficiaryAllocationValue {
  if (!rows || rows.length === 0) return defaultBeneficiaryAllocation();
  const toRow = (r: any): BeneficiaryRow => ({
    firstName: r.firstName ?? "",
    lastName: r.lastName ?? "",
    relationship: r.relationship ?? "",
    pct: Math.round(Number(r.percentage) || 0),
  });
  const bySeq = (a: any, b: any) => (a.sequenceOrder ?? 0) - (b.sequenceOrder ?? 0);
  // Only primaries are exposed today; ignore any legacy contingent rows.
  const primary = rows.filter((r) => r.beneficiaryType === "primary").sort(bySeq).map(toRow);
  return { primary: primary.length ? primary : [emptyRow(100)] };
}
